import type { InUseDetails, Reason } from '@pb/shared';

export class ApiError extends Error {
  readonly status: number;
  readonly reason: Reason | 'unauthenticated' | 'network' | 'unknown';
  readonly details: unknown;

  constructor(status: number, reason: ApiError['reason'], message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.reason = reason;
    this.details = details;
  }

  get inUse(): InUseDetails | null {
    if (this.reason !== 'in_use' || this.details === null || typeof this.details !== 'object') return null;
    return this.details as InUseDetails;
  }
}

type UnauthenticatedHandler = () => void;

let unauthenticatedHandler: UnauthenticatedHandler = () => {
  const next = window.location.pathname + window.location.search;
  window.location.assign(`/login?next=${encodeURIComponent(next)}`);
};

export function setUnauthenticatedHandler(handler: UnauthenticatedHandler): void {
  unauthenticatedHandler = handler;
}

const FALLBACK_MESSAGES: Record<number, string> = {
  400: 'The request was not accepted.',
  401: 'Your session has ended. Sign in again.',
  403: 'You are not allowed to do this.',
  404: 'This item does not exist or is no longer available.',
  409: 'This change conflicts with the current state.',
  413: 'The file is larger than 100 MB.',
  422: 'The request could not be processed.',
  429: 'Too many requests. Wait a moment and try again.',
};

function parseError(status: number, text: string, statusText: string): ApiError {
  let body: { reason?: string; error?: string; message?: string; details?: unknown } = {};
  try {
    if (text) body = JSON.parse(text) as typeof body;
  } catch {
    body = {};
  }
  const reason = (body.reason ?? (status === 401 ? 'unauthenticated' : body.error) ?? 'unknown') as ApiError['reason'];
  const message =
    body.message ?? FALLBACK_MESSAGES[status] ?? `The server answered ${status}${statusText ? ` ${statusText}` : ''}.`;
  return new ApiError(status, reason, message, body.details);
}

function parseBody<T>(text: string): T {
  if (!text) return undefined as T;
  return JSON.parse(text) as T;
}

export interface RequestOptions {
  skipAuthRedirect?: boolean;
}

export async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options: RequestOptions = {},
): Promise<T> {
  const init: RequestInit = { method, credentials: 'same-origin', headers: { Accept: 'application/json' } };
  if (method !== 'GET' && method !== 'HEAD') {
    init.headers = { Accept: 'application/json', 'Content-Type': 'application/json' };
    init.body = JSON.stringify(body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, init);
  } catch {
    throw new ApiError(0, 'network', 'The server could not be reached. Check your connection and try again.');
  }
  const text = await res.text();
  if (!res.ok) {
    const error = parseError(res.status, text, res.statusText);
    if (res.status === 401 && !options.skipAuthRedirect) unauthenticatedHandler();
    throw error;
  }
  return parseBody<T>(text);
}

export const get = <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options);
export const post = <T>(path: string, body?: unknown, options?: RequestOptions) =>
  request<T>('POST', path, body, options);
export const put = <T>(path: string, body?: unknown) => request<T>('PUT', path, body);
export const patch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, body);
export const del = <T>(path: string, body?: unknown) => request<T>('DELETE', path, body);

export function asList<T>(value: T[] | { items: T[] } | null | undefined): T[] {
  if (Array.isArray(value)) return value;
  if (value && Array.isArray((value as { items: T[] }).items)) return (value as { items: T[] }).items;
  return [];
}

export function query(params: Record<string, string | number | boolean | undefined | null | number[]>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      parts.push(`${encodeURIComponent(key)}=${value.join(',')}`);
      continue;
    }
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

export function uploadFile<T>(path: string, file: File, onProgress: (fraction: number) => void): UploadHandle<T> {
  const xhr = new XMLHttpRequest();
  const promise = new Promise<T>((resolve, reject) => {
    if (file.size > MAX_UPLOAD_BYTES) {
      reject(new ApiError(413, 'upload_too_large', 'The file is larger than 100 MB.'));
      return;
    }
    xhr.open('POST', `/api${path}`);
    xhr.withCredentials = true;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) onProgress(event.loaded / event.total);
    };
    xhr.onload = () => {
      const text = xhr.responseText;
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(1);
        try {
          resolve(parseBody<T>(text));
        } catch {
          reject(new ApiError(xhr.status, 'unknown', 'The server answered with an unreadable response.'));
        }
        return;
      }
      const error = parseError(xhr.status, text, xhr.statusText);
      if (xhr.status === 401) unauthenticatedHandler();
      reject(error);
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'The upload failed. Check your connection and try again.'));
    xhr.onabort = () => reject(new ApiError(0, 'network', 'The upload was cancelled.'));
    xhr.send(file);
  });
  return { promise, abort: () => xhr.abort() };
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
